/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import theme from '../../theme';
import { ThemeProvider } from 'emotion-theming'
import { Global, css } from '@emotion/core'

import SpaceMonoBoldWoff2 from "../fonts/SpaceMono-Bold.woff";
import SpaceMonoBoldWoff from "../fonts/SpaceMono-Bold.woff";
import SpaceMonoRegularWoff2 from "../fonts/SpaceMono-Regular.woff";
import SpaceMonoRegularWoff from "../fonts/SpaceMono-Regular.woff";

import NimbusSanLBoldWoff2 from "../fonts/NimbusSanL-Bold.woff2";
import NimbusSanLBoldWoff from "../fonts/NimbusSanL-Bold.woff";
import NimbusSanLReguWoff2 from "../fonts/NimbusSanL-Regu.woff2";
import NimbusSanLReguWoff from "../fonts/NimbusSanL-Regu.woff";

const globalStyles = css`

  @font-face {
    font-family: 'Space Mono';
    src: url(${SpaceMonoRegularWoff2}) format('woff2'), url(${SpaceMonoRegularWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
  }
  @font-face {
    font-family: 'Space Mono';
    src: url(${SpaceMonoBoldWoff2}) format('woff2'), url(${SpaceMonoBoldWoff}) format('woff');
    font-weight: 700;
    font-style: normal;
  }

  @font-face {
    font-family: 'NimbusSanL';
    src: url(${NimbusSanLReguWoff2}) format('woff2'), url(${NimbusSanLReguWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'NimbusSanL';
    src: url(${NimbusSanLBoldWoff2}) format('woff2'), url(${NimbusSanLBoldWoff}) format('woff');
    font-weight: bold;
    font-style: normal;
  }

  body {
    padding: 0;
    margin: 0;
    background-color: ${theme.colors.background};
    font-family: ${theme.fonts.body};
    font-size: 18px;
    line-height: ${theme.lineHeights.body};
    scroll-behavior: smooth;
  }

  h1 {
    font-family: ${theme.fonts.heading};
    font-size: 60px;
  }

  h2 {
    font-family: ${theme.fonts.heading};
    font-size: 28px;
    line-height: ${theme.headings.h2.lineHeight};
  }

  h3 {
    font-family: ${theme.fonts.heading};
    font-size: 22px;
    line-height: ${theme.headings.h3.lineHeight};
  }

  h4 {
    font-family: ${theme.fonts.heading};
    font-size: 20px;
    line-height: ${theme.headings.h4.lineHeight};
  }

  h5 {
    font-family: ${theme.fonts.heading};
    font-size: ${theme.headings.h5.fontSize}px;
  }

  h6 {
    font-family: ${theme.fonts.heading};
    font-size: ${theme.headings.h6.fontSize}px;
  }

  @media (min-width: ${theme.breakpoints[1]}) {
    body {
      font-size: ${theme.fontSizeBase}px;
    }
    h1 {
      font-size: ${theme.headings.h1.fontSize}px;
    }
    h2 {
      font-size: ${theme.headings.h2.fontSize}px;
    }
    h3 {
      font-size: ${theme.headings.h3.fontSize}px;
    }
    h4 {
      font-size: ${theme.headings.h4.fontSize}px;
    }
    h5 {
      font-size: ${theme.headings.h5.fontSize}px;
    }
    h6 {
      font-size: ${theme.headings.h6.fontSize}px;
    }
  }

  .fw-300 {
    font-weight: 300;
  }

  .fw-400 {
    font-weight: 400;
  }

  .fw-500 {
    font-weight: 500;
  }

  .fw-600 {
    font-weight: 600;
  }

  .fw-700 {
    font-weight: 700;
  }

  .fw-800 {
    font-weight: 800;
  }

  .fw-900 {
    font-weight: 900;
  }

  .ff-sans {
    font-family: ${theme.fonts.body};
  }

  .ff-monospace {
    font-family: ${theme.fonts.monospace};
  }

  .color-primary {
    color: ${theme.colors.primary};
  }

  .color-secondary {
    color: ${theme.colors.secondary};
  }

  @keyframes buttonsBaseLinkHover {
    from {
      transform: scale3d(0.0, 1.0, 1.0);
    }
    to {
      transform: scale3d(1.0, 1.0, 1.0);
    }
  }
  @keyframes buttonsBaseLinkOut {
    from {
      transform: translate3d(0%, 0, 0) scale3d(1.0, 1.0, 1.0);
    }
    to {
      transform: translate3d(100%, 0, 0) scale3d(0.0, 1.0, 1.0);
    }
  }

  @keyframes buttonsCtaOutlinedHover {
    from {
      transform: scale3d(1.0, 0.0, 1.0);
    }
    to {
      transform: scale3d(1.0, 1.0, 1.0);
    }
  }
  @keyframes buttonsCtaOutlinedOut {
    from {
      transform: translate3d(0, 0%, 0) scale3d(1.0, 1.0, 1.0);
    }
    to {
      transform: translate3d(0, 100%, 0) scale3d(1.0, 0.0, 1.0);
    }
  }

  @keyframes tabsImages {
    0% {
      transform: scale3d(0.9, 0.9, 1.0);
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    100% {
      transform: scale3d(1.0, 1.0, 1.0);
    }
  }

`

export const mergePageBuilderThemeData = (theme, data) => {
  const { colorPrimary, colorSecondary } = data.wpgraphql.page.page_builder

  // inject new colors
  theme.colors = {
    ...theme.colors,
    ...{
      primary:  colorPrimary,
      secondary: colorSecondary
    }
  };

  return theme;
}

const Layout = ({ children }) => {
  const data = useStaticQuery(graphql`
    query {
      wpgraphql {
        page: pageBy(uri: "esma-3d") {
          title
          id
          page_builder {
            siteTitle
            colorPrimary
            colorSecondary
          }
        }
      }
    }
  `)

  // get theme values from page_builder
  let myTheme = mergePageBuilderThemeData(theme, data)

  return (
    <ThemeProvider theme={myTheme}>
      <Global styles={globalStyles} />
      <main>{children}</main>
    </ThemeProvider>

  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
